import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { AdminPanelSettings as AdminIcon } from '@mui/icons-material';
import { keyframes } from '@mui/system';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(16px); }
  100% { opacity: 1; transform: translateY(0); }
`;

export default function Setup() {
  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginGerado, setLoginGerado] = useState('');
  const [backgroundUrl, setBackgroundUrl] = useState('');
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const cacheBust = (url) => (url ? `${url}?t=${Date.now()}` : '');

  useEffect(() => {
    let active = true;
    const loadBackground = async () => {
      try {
        const response = await api.get('/branding/login-background');
        if (active) {
          setBackgroundUrl(cacheBust(response.data.url || ''));
        }
      } catch (error) {
        console.error('Erro ao carregar background:', error);
      }
    };

    loadBackground();
    return () => {
      active = false;
    };
  }, []);

  const gerarLogin = (nomeCompleto) => {
    if (!nomeCompleto || nomeCompleto.trim().length === 0) {
      setLoginGerado('');
      return;
    }

    const partesNome = nomeCompleto.trim().split(' ');
    let login;
    if (partesNome.length >= 2) {
      const primeiroNome = partesNome[0].toLowerCase();
      const ultimoNome = partesNome[partesNome.length - 1].toLowerCase();
      login = `${primeiroNome}.${ultimoNome}`;
    } else {
      login = nomeCompleto.toLowerCase().replace(/\s+/g, '.');
    }
    login = login.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    login = login.replace(/[^a-z0-9.]/g, '');
    setLoginGerado(login);
  };

  const handleNomeChange = (e) => {
    const novoNome = e.target.value;
    setNome(novoNome);
    gerarLogin(novoNome);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!nome || nome.trim().length === 0) {
      toast.error('Digite seu nome completo');
      return;
    }

    if (!senha || senha.length < 6) {
      toast.error('Senha deve ter no minimo 6 caracteres');
      return;
    }

    if (senha !== confirmarSenha) {
      toast.error('Senhas nao conferem');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/setup-admin', {
        nome: nome.trim(),
        senha,
      });

      const { token, usuario } = response.data;
      setSession(usuario, token);

      toast.success(`Bem-vindo, ${usuario.nome}! Seu login e: ${usuario.email}`);

      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error) {
      console.error('Erro ao configurar admin:', error);
      toast.error(error.response.data.error || 'Erro ao criar administrador');
    } finally {
      setLoading(false);
    }
  };

  const backgroundImage = backgroundUrl
    ? `linear-gradient(120deg, rgba(8, 23, 33, 0.85), rgba(11, 38, 52, 0.75)), url(${backgroundUrl})`
    : 'radial-gradient(circle at 90% 10%, rgba(199, 161, 93, 0.25), transparent 45%), linear-gradient(140deg, #0b1f2a, #0d2536 55%, #0b1f2a)';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 520px' },
        backgroundImage,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: 'common.white',
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
        }}
      >
        <Box sx={{ animation: `${fadeInUp} 600ms ease` }}>
          <Typography variant="overline" sx={{ letterSpacing: 3, opacity: 0.7 }}>
            Configuração Inicial
          </Typography>
          <Typography variant="h2" sx={{ fontWeight: 600, mt: 2 }}>
            Defina o primeiro administrador
          </Typography>
          <Typography sx={{ mt: 2, maxWidth: 420, opacity: 0.8 }}>
            O acesso inicial define as permissoes e garante que o sistema esteja pronto para sua equipe.
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ opacity: 0.6 }}>
          Você poderá criar novos usuários depois em Configurações.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 5 },
          backdropFilter: { xs: 'blur(0px)', md: 'blur(6px)' },
        }}
      >
        <Paper
          elevation={8}
          sx={{
            width: '100%',
            maxWidth: 480,
            p: 4,
            borderRadius: 3,
            animation: `${fadeInUp} 500ms ease`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                bgcolor: 'secondary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'common.white',
              }}
            >
              <AdminIcon sx={{ fontSize: 30 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Configuração inicial
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Crie o administrador para entrar no sistema
              </Typography>
            </Box>
          </Box>

          <Stepper activeStep={0} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Criar administrador</StepLabel>
            </Step>
            <Step>
              <StepLabel>Ajustes finais</StepLabel>
            </Step>
          </Stepper>

          <Alert severity="info" sx={{ mb: 3 }}>
            O login sera gerado automaticamente no formato nome.sobrenome.
          </Alert>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="nome"
              label="Nome completo"
              name="nome"
              placeholder="Ex: Anderson Silva"
              autoFocus
              value={nome}
              onChange={handleNomeChange}
              helperText="Digite nome e sobrenome"
            />

            {loginGerado && (
              <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                Seu login sera: <strong>{loginGerado}</strong>
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              name="senha"
              label="Senha"
              type="password"
              id="senha"
              autoComplete="new-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              helperText="Minimo 6 caracteres"
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmarSenha"
              label="Confirmar senha"
              type="password"
              id="confirmarSenha"
              autoComplete="new-password"
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3 }}
              disabled={loading}
            >
                {loading ? 'Criando...' : 'Criar administrador e entrar'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
